import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { useStore } from '../../store/useStore';
import { leadService } from '../../lib/leadService';
import {
    Lead, LeadStage, LeadSource, LEAD_STAGES, LEAD_SOURCE_KEYS, LEAD_SOURCE_LABELS,
} from '../../types/leads.types';

interface Profile {
    id: string;
    full_name: string | null;
    email: string;
}

interface LeadFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    lead?: Lead | null;
    onSaved: () => void;
}

const emptyForm = {
    full_name: '',
    company_name: '',
    email: '',
    phone: '',
    city: '',
    province: '',
    source: 'personale' as LeadSource,
    stage: 'nuovo' as LeadStage,
    assigned_to: '',
    notes: '',
};

export function LeadFormDialog({ open, onOpenChange, lead, onSaved }: LeadFormDialogProps) {
    const { addNotification } = useStore();
    const [form, setForm] = useState(emptyForm);
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            leadService.getAssignableProfiles().then(setProfiles).catch(() => setProfiles([]));
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            if (lead) {
                setForm({
                    full_name: lead.full_name || '',
                    company_name: lead.company_name || '',
                    email: lead.email || '',
                    phone: lead.phone || '',
                    city: lead.city || '',
                    province: lead.province || '',
                    source: lead.source,
                    stage: lead.stage,
                    assigned_to: lead.assigned_to || '',
                    notes: lead.notes || '',
                });
            } else {
                setForm(emptyForm);
            }
        }
    }, [open, lead]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.full_name.trim() && !form.company_name.trim()) {
            addNotification({ type: 'error', title: 'Nome o azienda richiesti' });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                full_name: form.full_name.trim() || null,
                company_name: form.company_name.trim() || null,
                email: form.email.trim() || null,
                phone: form.phone.trim() || null,
                city: form.city.trim() || null,
                province: form.province.trim() || null,
                source: form.source,
                stage: form.stage,
                assigned_to: form.assigned_to || null,
                notes: form.notes.trim() || null,
            };

            if (lead) {
                await leadService.updateLead(lead.id, payload);
                addNotification({ type: 'success', title: 'Lead aggiornato' });
            } else {
                await leadService.createLead(payload);
                addNotification({ type: 'success', title: 'Lead creato' });
            }
            onSaved();
            onOpenChange(false);
        } catch (error) {
            console.error('Error saving lead:', error);
            const msg = (error as { message?: string; details?: string })?.message
                || (error as { details?: string })?.details
                || 'Impossibile salvare il lead';
            addNotification({ type: 'error', title: 'Errore', message: msg });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{lead ? 'Modifica Lead' : 'Nuovo Lead'}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="full_name">Nome e Cognome</Label>
                            <Input
                                id="full_name"
                                value={form.full_name}
                                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                placeholder="Mario Rossi"
                            />
                        </div>
                        <div>
                            <Label htmlFor="company_name">Azienda</Label>
                            <Input
                                id="company_name"
                                value={form.company_name}
                                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                                placeholder="Rossi S.r.l."
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="mario@example.com"
                            />
                        </div>
                        <div>
                            <Label htmlFor="phone">Telefono</Label>
                            <Input
                                id="phone"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                placeholder="+39 320 000 0000"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="city">Città</Label>
                            <Input
                                id="city"
                                value={form.city}
                                onChange={(e) => setForm({ ...form, city: e.target.value })}
                                placeholder="Pescara"
                            />
                        </div>
                        <div>
                            <Label htmlFor="province">Provincia</Label>
                            <Input
                                id="province"
                                value={form.province}
                                onChange={(e) => setForm({ ...form, province: e.target.value })}
                                placeholder="PE"
                                maxLength={2}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label>Provenienza</Label>
                            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v as LeadSource })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Provenienza" />
                                </SelectTrigger>
                                <SelectContent>
                                    {LEAD_SOURCE_KEYS.map((s) => (
                                        <SelectItem key={s} value={s}>{LEAD_SOURCE_LABELS[s]}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Stato pipeline</Label>
                            <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v as LeadStage })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Stato" />
                                </SelectTrigger>
                                <SelectContent>
                                    {LEAD_STAGES.map((s) => (
                                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label>Assegnato a</Label>
                            <Select
                                value={form.assigned_to || 'none'}
                                onValueChange={(v) => setForm({ ...form, assigned_to: v === 'none' ? '' : v })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Nessuno" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Nessuno</SelectItem>
                                    {profiles.map((p) => (
                                        <SelectItem key={p.id} value={p.id}>
                                            {p.full_name || p.email}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="notes">Note</Label>
                        <Textarea
                            id="notes"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Note sul lead..."
                            rows={3}
                        />
                    </div>

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" variant="outline" disabled={saving}>Annulla</Button>
                        </DialogClose>
                        <Button type="submit" disabled={saving}>
                            {saving ? 'Salvataggio...' : lead ? 'Aggiorna' : 'Crea'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default LeadFormDialog;
